import { PropAsset } from './PropAsset';

// Bridge - Köprü
// Wooden or stone bridge that spans gaps/water
export class Bridge extends PropAsset {
  readonly id = 'bridge';
  readonly width = 100;
  readonly height = 60;
  readonly walkable = true;
  readonly collisionRadius = 0;
  readonly occludesPlayer = false;

  draw(ctx: CanvasRenderingContext2D, cx: number, cy: number, s: number, variant?: number): void {
    const v = variant || 0;

    const bridgeColors = [
      { main: '#8b7355', dark: '#5a4a3a', light: '#a08570', type: 'wood' },   // Wooden
      { main: '#7a7a7a', dark: '#5a5a5a', light: '#9a9a9a', type: 'stone' },  // Stone
      { main: '#6a5a4a', dark: '#4a3a2a', light: '#8a7a6a', type: 'rope' },   // Rope bridge
    ];
    const colors = bridgeColors[v % bridgeColors.length];

    const bridgeW = 90 * s;
    const bridgeH = 45 * s;
    const railH = 20 * s;

    // Shadow under bridge
    ctx.fillStyle = 'rgba(0,0,0,0.4)';
    ctx.beginPath();
    ctx.ellipse(cx, cy + 5 * s, bridgeW / 2, 15 * s, 0, 0, Math.PI * 2);
    ctx.fill();

    if (colors.type === 'wood' || colors.type === 'rope') {
      // Bridge deck (isometric)
      ctx.fillStyle = colors.main;
      ctx.beginPath();
      ctx.moveTo(cx - bridgeW / 2, cy);
      ctx.lineTo(cx, cy - bridgeH / 3);
      ctx.lineTo(cx + bridgeW / 2, cy);
      ctx.lineTo(cx, cy + bridgeH / 3);
      ctx.closePath();
      ctx.fill();

      // Plank lines
      ctx.strokeStyle = colors.dark;
      ctx.lineWidth = 1 * s;
      const plankCount = 8;
      for (let i = 1; i < plankCount; i++) {
        const t = i / plankCount;
        const px1 = cx - bridgeW / 2 + t * bridgeW / 2;
        const py1 = cy - t * bridgeH / 3;
        const px2 = cx + t * bridgeW / 2;
        const py2 = cy + (1 - t) * bridgeH / 3;

        ctx.beginPath();
        ctx.moveTo(px1, py1);
        ctx.lineTo(px2, py2);
        ctx.stroke();
      }

      // Side rails
      ctx.fillStyle = colors.dark;

      // Left rail
      ctx.beginPath();
      ctx.moveTo(cx - bridgeW / 2, cy);
      ctx.lineTo(cx - bridgeW / 2, cy - railH);
      ctx.lineTo(cx, cy - bridgeH / 3 - railH);
      ctx.lineTo(cx, cy - bridgeH / 3);
      ctx.closePath();
      ctx.fill();

      // Right rail
      ctx.beginPath();
      ctx.moveTo(cx, cy + bridgeH / 3);
      ctx.lineTo(cx, cy + bridgeH / 3 - railH);
      ctx.lineTo(cx + bridgeW / 2, cy - railH);
      ctx.lineTo(cx + bridgeW / 2, cy);
      ctx.closePath();
      ctx.fill();

      // Rail posts
      ctx.fillStyle = colors.light;
      const postCount = 4;
      for (let i = 0; i <= postCount; i++) {
        const t = i / postCount;
        // Left side
        const lx = cx - bridgeW / 2 + t * bridgeW / 2;
        const ly = cy - t * bridgeH / 3;
        ctx.fillRect(lx - 2 * s, ly - railH, 4 * s, railH);

        // Right side
        const rx = cx + t * bridgeW / 2;
        const ry = cy + (1 - t) * bridgeH / 3;
        ctx.fillRect(rx - 2 * s, ry - railH, 4 * s, railH);
      }

      // Rope details for rope bridge
      if (colors.type === 'rope') {
        ctx.strokeStyle = '#5a4a3a';
        ctx.lineWidth = 2 * s;
        ctx.setLineDash([4 * s, 4 * s]);

        ctx.beginPath();
        ctx.moveTo(cx - bridgeW / 2, cy - railH + 5 * s);
        ctx.quadraticCurveTo(cx - bridgeW / 4, cy - railH - 5 * s, cx, cy - bridgeH / 3 - railH + 5 * s);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(cx, cy + bridgeH / 3 - railH + 5 * s);
        ctx.quadraticCurveTo(cx + bridgeW / 4, cy - railH - 5 * s, cx + bridgeW / 2, cy - railH + 5 * s);
        ctx.stroke();

        ctx.setLineDash([]);
      }

    } else {
      // Stone bridge
      // Main deck with slight arch
      ctx.fillStyle = colors.dark;
      ctx.beginPath();
      ctx.moveTo(cx - bridgeW / 2, cy + 8 * s);
      ctx.quadraticCurveTo(cx, cy - 10 * s, cx + bridgeW / 2, cy + 8 * s);
      ctx.lineTo(cx + bridgeW / 2, cy);
      ctx.quadraticCurveTo(cx, cy - 18 * s, cx - bridgeW / 2, cy);
      ctx.closePath();
      ctx.fill();

      // Top surface
      ctx.fillStyle = colors.main;
      ctx.beginPath();
      ctx.moveTo(cx - bridgeW / 2, cy);
      ctx.quadraticCurveTo(cx, cy - 18 * s, cx + bridgeW / 2, cy);
      ctx.lineTo(cx + bridgeW / 2 - 8 * s, cy - 8 * s);
      ctx.quadraticCurveTo(cx, cy - 22 * s, cx - bridgeW / 2 + 8 * s, cy - 8 * s);
      ctx.closePath();
      ctx.fill();

      // Stone pattern
      ctx.strokeStyle = 'rgba(0,0,0,0.2)';
      ctx.lineWidth = 1 * s;
      for (let i = 1; i < 6; i++) {
        const t = i / 6;
        const x1 = cx - bridgeW / 2 + t * bridgeW;
        ctx.beginPath();
        ctx.moveTo(x1, cy);
        ctx.lineTo(x1 - 5 * s, cy - 15 * s + Math.abs(t - 0.5) * 10 * s);
        ctx.stroke();
      }

      // Low stone walls on sides
      ctx.fillStyle = colors.light;
      ctx.beginPath();
      ctx.moveTo(cx - bridgeW / 2, cy);
      ctx.lineTo(cx - bridgeW / 2, cy - 12 * s);
      ctx.lineTo(cx - bridgeW / 2 + 8 * s, cy - 18 * s);
      ctx.lineTo(cx - bridgeW / 2 + 8 * s, cy - 6 * s);
      ctx.closePath();
      ctx.fill();

      ctx.beginPath();
      ctx.moveTo(cx + bridgeW / 2, cy);
      ctx.lineTo(cx + bridgeW / 2, cy - 12 * s);
      ctx.lineTo(cx + bridgeW / 2 - 8 * s, cy - 18 * s);
      ctx.lineTo(cx + bridgeW / 2 - 8 * s, cy - 6 * s);
      ctx.closePath();
      ctx.fill();
    }
  }
}

// Ramp - Rampa
// Inclined surface for walking between elevations
export class Ramp extends PropAsset {
  readonly id = 'ramp';
  readonly width = 80;
  readonly height = 50;
  readonly walkable = true;
  readonly collisionRadius = 0;
  readonly occludesPlayer = false;

  draw(ctx: CanvasRenderingContext2D, cx: number, cy: number, s: number, variant?: number): void {
    const v = variant || 0;

    const rampColors = [
      { main: '#7a7060', dark: '#5a5040', light: '#9a9080' },  // Stone
      { main: '#8b7355', dark: '#5a4a3a', light: '#a08570' },  // Wood
      { main: '#5a6a5a', dark: '#3a4a3a', light: '#7a8a7a' },  // Earth/grass
    ];
    const colors = rampColors[v % rampColors.length];

    const rampW = 70 * s;
    const rampH = 35 * s;
    const rampDepth = 25 * s;

    // Shadow
    ctx.fillStyle = 'rgba(0,0,0,0.25)';
    ctx.beginPath();
    ctx.ellipse(cx, cy + 3 * s, rampW / 2, 12 * s, 0, 0, Math.PI * 2);
    ctx.fill();

    // Ramp side (left - darker)
    ctx.fillStyle = colors.dark;
    ctx.beginPath();
    ctx.moveTo(cx - rampW / 2, cy);
    ctx.lineTo(cx - rampW / 2, cy - rampH);
    ctx.lineTo(cx, cy - rampH - rampDepth / 2);
    ctx.lineTo(cx, cy - rampDepth / 2);
    ctx.closePath();
    ctx.fill();

    // Ramp surface (top - main color, sloped)
    ctx.fillStyle = colors.main;
    ctx.beginPath();
    ctx.moveTo(cx - rampW / 2, cy - rampH);
    ctx.lineTo(cx, cy - rampH - rampDepth / 2);
    ctx.lineTo(cx + rampW / 2, cy - rampDepth / 2); // Lower at front
    ctx.lineTo(cx, cy);
    ctx.closePath();
    ctx.fill();

    // Front edge
    ctx.fillStyle = colors.light;
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(cx + rampW / 2, cy - rampDepth / 2);
    ctx.lineTo(cx + rampW / 2, cy - rampDepth / 2 - 5 * s);
    ctx.lineTo(cx, cy - 5 * s);
    ctx.closePath();
    ctx.fill();

    // Surface details
    if (v === 0) {
      // Stone - add lines
      ctx.strokeStyle = 'rgba(0,0,0,0.2)';
      ctx.lineWidth = 1 * s;
      for (let i = 1; i < 4; i++) {
        const t = i / 4;
        ctx.beginPath();
        ctx.moveTo(cx - rampW / 2 + t * rampW / 2, cy - rampH + t * rampH);
        ctx.lineTo(cx + t * rampW / 2, cy - rampH - rampDepth / 2 + t * (rampH + rampDepth / 2));
        ctx.stroke();
      }
    } else if (v === 1) {
      // Wood - add planks
      ctx.strokeStyle = colors.dark;
      ctx.lineWidth = 1 * s;
      for (let i = 1; i < 6; i++) {
        const t = i / 6;
        const x1 = cx - rampW / 2 * (1 - t);
        const y1 = cy - rampH * (1 - t);
        const x2 = cx + rampW / 2 * t;
        const y2 = cy - rampDepth / 2 * t;
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
      }
    } else {
      // Earth - add grass tufts at top
      ctx.fillStyle = '#5a8a5a';
      for (let i = 0; i < 3; i++) {
        const gx = cx - rampW / 3 + i * rampW / 4;
        const gy = cy - rampH - rampDepth / 3;
        ctx.beginPath();
        ctx.moveTo(gx - 2 * s, gy);
        ctx.lineTo(gx, gy - 5 * s);
        ctx.lineTo(gx + 2 * s, gy);
        ctx.closePath();
        ctx.fill();
      }
    }

    // Highlight at high edge
    ctx.strokeStyle = 'rgba(255,255,255,0.3)';
    ctx.lineWidth = 1.5 * s;
    ctx.beginPath();
    ctx.moveTo(cx - rampW / 2, cy - rampH);
    ctx.lineTo(cx, cy - rampH - rampDepth / 2);
    ctx.stroke();
  }
}
